import { INTERCOM_SURVEY } from 'utils/constants/queryParam';

const PENDING_SURVEY_STORAGE_KEY = 'pending_intercom_survey';

const NUMERIC_ID_PATTERN = /^\d+$/;

/**
 * Captures an `intercom_survey` query param from a deep link (e.g. a Salesforce CSAT email)
 * before the app renders, stashing it in sessionStorage so it survives the login flow.
 * The param is stripped from the URL, and a bare `/ticket` deep link is rewritten to `/`
 * so the user lands in the app instead of the unauthenticated ticket screen.
 *
 * Must be called before the router mounts; the survey is fired later by the Intercom
 * middleware via `consumePendingIntercomSurveyId` once the user is identified.
 */
export function captureIntercomSurveyDeepLink() {
  const { pathname, search, hash } = window.location;
  const params = new URLSearchParams(search);
  const surveyId = params.get(INTERCOM_SURVEY);

  if (surveyId === null) {
    return;
  }

  params.delete(INTERCOM_SURVEY);

  if (NUMERIC_ID_PATTERN.test(surveyId)) {
    try {
      window.sessionStorage.setItem(PENDING_SURVEY_STORAGE_KEY, surveyId);
    } catch {
      // sessionStorage unavailable; the link degrades to a normal login.
    }
  }

  const remainingSearch = params.toString();
  const isBareTicketDeepLink = pathname.startsWith('/ticket') && remainingSearch === '';
  const newPath = isBareTicketDeepLink ? '/' : pathname;

  window.history.replaceState(
    window.history.state,
    '',
    `${newPath}${remainingSearch ? `?${remainingSearch}` : ''}${hash}`
  );
}

/**
 * Returns a pending survey id captured by `captureIntercomSurveyDeepLink` and clears it,
 * or null if none is pending. Call only when Intercom is booted with the identified user,
 * since surveys with audience rules require identity.
 */
export function consumePendingIntercomSurveyId(): number | null {
  try {
    const value = window.sessionStorage.getItem(PENDING_SURVEY_STORAGE_KEY);

    if (value === null) {
      return null;
    }

    window.sessionStorage.removeItem(PENDING_SURVEY_STORAGE_KEY);

    return NUMERIC_ID_PATTERN.test(value) ? Number(value) : null;
  } catch {
    return null;
  }
}
